import React, { FC, useCallback, useState } from 'react'
import {
  Button,
  ButtonGroup,
  KennoFile,
  TextArea,
  TextInput,
  Toggle,
  ToggleDropdownSelector,
} from '@pandiafi/design-system'
import {
  Field,
  FieldProps,
  Form,
  Formik,
  FormikProps,
  FormikTouched,
  setNestedObjectValues,
} from 'formik'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import initialValues, { FormValues } from './initialValues'
import { formatFormValues, useDefectClasses, useDefectLocations } from './utils'
import { Contract, UserMetadata } from '../../../common/validations'
import {
  DefectNoticeDefectClasses,
  createDefectNoticeRequestSchema,
  DefectNoticeDetails,
  NoticeStatus,
} from '../../../common/validations/defectNotice'
import { DefectNoticePreview } from './Preview'
import { DefectNoticeViewState } from '.'
import { API_ENDPOINTS } from '../../../common/apiEndpoints'
import { useAPIPatchDefectNotice } from '../../api/apiHooks/useAPIPatchDefectNotice'
import { useContractPaths } from '../../hooks/useContractPaths/useContractPaths'

import FormFieldWrapper from 'components/FormField'
import { UploadFiles } from 'components/UploadFiles'
import { validateFormValues } from 'utils'
import { useAPIPostDefectNotice } from 'api/apiHooks/useAPIPostDefectNotice'

interface DefectNoticeFormProps {
  contract: Contract
  defectClasses: DefectNoticeDefectClasses
  defectNotice?: DefectNoticeDetails | null
  view: DefectNoticeViewState
  setView: (view: DefectNoticeViewState) => void
  userMetadata: UserMetadata
}

export const DefectNoticeForm: FC<DefectNoticeFormProps> = ({
  contract,
  defectClasses,
  defectNotice,
  view,
  setView,
  userMetadata,
}) => {
  const normalizeSelectValue = (value?: string | number | null) =>
    value === undefined || value === null ? '' : String(value)
  const { t } = useTranslation(['common', 'defectNotice'])
  const { buttonItems, listItems } = useDefectClasses(defectClasses)
  const defectLocations = useDefectLocations(contract)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [uploadOngoing, setUploadOngoing] = useState<boolean>(false)
  const { defectNoticePath } = useContractPaths()
  const { submit, isSubmitting } = useAPIPostDefectNotice({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          `${API_ENDPOINTS.CONTRACTS}/${contract.uuid}/defect-notices`,
        ],
      })
      setView('success')
    },
  })
  const { submit: updateSubmit, isSubmitting: updateIsSubmitting } =
    useAPIPatchDefectNotice(defectNotice?.uuid || '', {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            `${API_ENDPOINTS.CONTRACTS}/${contract.uuid}/defect-notices`,
          ],
        })
        queryClient.invalidateQueries({
          queryKey: [
            `${API_ENDPOINTS.CONTRACTS}/${contract.uuid}/defect-notices/${defectNotice?.uuid}`,
          ],
        })
        setView('success')
      },
    })

  const handleOnFilesUploaded = useCallback(
    (newFiles: KennoFile[], form: FormikProps<FormValues>): void => {
      const { files } = form.values

      form.setFieldValue('files', [...files, ...newFiles])
    },
    []
  )

  const handleOnDeleteUploadedFile = useCallback(
    (fileToRemove: KennoFile, form: FormikProps<FormValues>): void => {
      const { files } = form.values
      form.setFieldValue(
        'files',
        (files || []).filter((f) => f.uuid !== fileToRemove.uuid)
      )
    },
    []
  )

  const handleValidate = (formValues: FormValues) => {
    const validationErrors = validateFormValues(
      formatFormValues(formValues, contract.uuid),
      createDefectNoticeRequestSchema
    )

    return validationErrors
  }

  const handlePreview = async (form: FormikProps<FormValues>) => {
    const errors = await form.validateForm()

    if (Object.keys(errors).length > 0) {
      // Mark fields with errors as touched to show error messages
      await form.setTouched(
        setNestedObjectValues<FormikTouched<FormValues>>(errors, true)
      )
      setTimeout(() => {
        const errorElement = document.querySelector('.validation-error')
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          })
        }
      }, 100)
    } else {
      setView('preview')
      requestAnimationFrame(() => {
        setTimeout(() => {
          const previewElement = document.querySelector('form')
          if (previewElement) {
            previewElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
          }
        }, 100)
      })
    }
  }

  const handleSubmit = (values: FormValues) => {
    if (defectNotice) {
      updateSubmit(formatFormValues(values, contract.uuid))
    } else {
      submit(formatFormValues(values, contract.uuid))
    }
  }

  return (
    <Formik
      initialValues={initialValues(defectNotice)}
      onSubmit={handleSubmit}
      validate={handleValidate}
      enableReinitialize
    >
      {(form: FormikProps<FormValues>) => (
        <Form>
          {view === 'preview' ? (
            <DefectNoticePreview
              userMetadata={userMetadata}
              contract={contract}
              formValues={form.values}
              isSubmitting={isSubmitting || updateIsSubmitting}
              submit={() => form.submitForm()}
              cancel={() => setView('form')}
              editMode={!!defectNotice}
            />
          ) : (
            <>
              <Field name="defectLocation">
                {({
                  field,
                  meta,
                }: FieldProps<FormValues['defectLocation']>) => (
                  <FormFieldWrapper
                    name={field.name}
                    label={t('defectNotice:labels.defectLocation')}
                    meta={meta}
                    labelBold
                    required
                  >
                    <ButtonGroup
                      name={field.name}
                      suggestions={defectLocations}
                      onChange={(value) => {
                        form.setFieldValue(field.name, value)
                      }}
                      value={meta.value}
                      sameSize
                      fullwidth
                      disabled={!!defectNotice}
                    />
                  </FormFieldWrapper>
                )}
              </Field>
              <Field name="defectClassId">
                {({ field, meta }: FieldProps<FormValues['defectClassId']>) => (
                  <FormFieldWrapper
                    name={field.name}
                    label={t('defectNotice:labels.defectClass')}
                    meta={meta}
                    className="mt-8"
                    labelBold
                    required
                  >
                    <ToggleDropdownSelector
                      buttonItems={buttonItems}
                      listItems={listItems}
                      onChange={(value) => {
                        form.setFieldValue(
                          field.name,
                          normalizeSelectValue(value)
                        )
                      }}
                      selected={normalizeSelectValue(meta.value)}
                      placeholder={t('defectNotice:placeholders.defectClass')}
                    />
                  </FormFieldWrapper>
                )}
              </Field>

              <Field name="title">
                {({ field, meta }: FieldProps<FormValues['title']>) => (
                  <FormFieldWrapper
                    name={field.name}
                    label={t('defectNotice:labels.description')}
                    meta={meta}
                    className="mt-8"
                    labelBold
                    required
                  >
                    <TextInput
                      {...field}
                      maxLength={100}
                      value={meta.value}
                      rounded="rounded-none"
                      className="border-common-gray-4"
                      placeholder={t('defectNotice:placeholders.title')}
                    />
                  </FormFieldWrapper>
                )}
              </Field>
              <Field name="description">
                {({ field, meta }: FieldProps<FormValues['description']>) => (
                  <FormFieldWrapper name={field.name} meta={meta}>
                    <TextArea
                      onChange={(e) =>
                        form.setFieldValue(field.name, e.target.value)
                      }
                      value={meta.value}
                      rounded="rounded-none"
                      className="border-common-gray-4 border-t-0 mb-8"
                      placeholder={t('defectNotice:placeholders.description')}
                    />
                  </FormFieldWrapper>
                )}
              </Field>
              <Field name="files">
                {({ field, meta }: FieldProps<FormValues['files']>) => (
                  <FormFieldWrapper name={field.name} meta={meta}>
                    <UploadFiles
                      className="my-8"
                      id="files"
                      files={meta.value || []}
                      onFilesUploaded={(newFiles) =>
                        handleOnFilesUploaded(newFiles, form)
                      }
                      onDeleteFile={(fileToRemove) =>
                        handleOnDeleteUploadedFile(fileToRemove, form)
                      }
                      onUploadOngoing={(isUploadOngoing) =>
                        setUploadOngoing(isUploadOngoing)
                      }
                    />
                  </FormFieldWrapper>
                )}
              </Field>
              <div className="phone:mt-8 min-tablet:mt-16 flex flex-col items-end space-y-2.5">
                <Field name="masterKeyAccessAllowed">
                  {({
                    field,
                    meta,
                  }: FieldProps<FormValues['masterKeyAccessAllowed']>) => (
                    <FormFieldWrapper name={field.name} meta={meta} labelBold>
                      <Toggle
                        checked={meta.value}
                        labelPosition="left"
                        onChange={(value) =>
                          form.setFieldValue(field.name, value)
                        }
                      >
                        {t('defectNotice:labels.masterKeyAccessAllowed')}
                      </Toggle>
                    </FormFieldWrapper>
                  )}
                </Field>
                <Field name="hasPetsInApartment">
                  {({
                    field,
                    meta,
                  }: FieldProps<FormValues['hasPetsInApartment']>) => (
                    <FormFieldWrapper name={field.name} meta={meta} labelBold>
                      <Toggle
                        checked={meta.value}
                        labelPosition="left"
                        onChange={(value) =>
                          form.setFieldValue(field.name, value)
                        }
                      >
                        {t('defectNotice:labels.hasPetsInApartment')}
                      </Toggle>
                    </FormFieldWrapper>
                  )}
                </Field>
              </div>
              <div className="mt-16 flex items-center justify-end">
                {defectNotice ? (
                  <Button
                    type="button"
                    variant="cancel"
                    onClick={() => {
                      if (defectNotice) {
                        navigate(defectNoticePath)
                      } else {
                        setView('form')
                      }
                    }}
                  >
                    {t('defectNotice:actions.back')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="cancel"
                    onClick={() => {
                      window.scrollTo(0, 0)
                      form.resetForm()
                    }}
                  >
                    {t('defectNotice:actions.resetForm')}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => handlePreview(form)}
                  disabled={
                    isSubmitting ||
                    uploadOngoing ||
                    updateIsSubmitting ||
                    !form.dirty ||
                    defectNotice?.status === NoticeStatus.COMPLETED
                  }
                >
                  {t('common:preview')}
                </Button>
              </div>
            </>
          )}
        </Form>
      )}
    </Formik>
  )
}
